import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  PAYOUT_WIZARD_COUNTRIES,
  RAILS_BY_RUNTIME_CORRIDOR,
  RUNTIME_CORRIDOR_ISO3,
} from "@/modules/dashboard/dispersions/constants/dispersions.constants";
import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import type { UseOrganizationsCatalogResult } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { corridorValue } from "../../utils/batchSetupSchema";

const ALL_CORRIDORS = "__all__";

export interface BatchesFiltersProps {
  /** INTERNAL only — null hides the organization filter. */
  catalog: UseOrganizationsCatalogResult | null;
  organizationId: string | null;
  onOrganizationChange: (organizationId: string | null) => void;
  corridor: string | null;
  onCorridorChange: (corridor: string | null) => void;
}

/** Organization (staff) and corridor filters of the list; the status filter is the KPI strip. */
export function BatchesFilters({
  catalog,
  organizationId,
  onOrganizationChange,
  corridor,
  onCorridorChange,
}: BatchesFiltersProps) {
  const { t } = useTranslations("payoutBatches");
  const countryLabel = useCountryLabel();

  const corridorOptions = RUNTIME_CORRIDOR_ISO3.flatMap((country) =>
    RAILS_BY_RUNTIME_CORRIDOR[country].map((rail) => {
      const fallback = PAYOUT_WIZARD_COUNTRIES.find((entry) => entry.code === country)?.name ?? country;
      return {
        value: corridorValue(country, rail),
        label: `${countryLabel(country, fallback)} · ${formatRail(rail)} · ${CURRENCY_BY_RUNTIME_CORRIDOR[country]}`,
      };
    }),
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:max-w-3xl">
      {catalog && (
        <OrganizationScopePicker
          id="payout-batches-organization"
          value={organizationId}
          onValueChange={onOrganizationChange}
          catalog={catalog}
          labels={{
            label: t("org_picker_label"),
            placeholder: t("org_picker_placeholder"),
            loading: t("org_picker_loading"),
            empty: t("org_picker_empty"),
            searching: t("org_picker_searching"),
            searchError: t("org_picker_search_error"),
            catalogError: t("org_picker_catalog_error"),
            catalogTruncated: t("org_picker_catalog_truncated"),
            globalOption: t("org_picker_global_option"),
            globalHint: t("org_picker_global_hint"),
            orgHint: t("org_picker_org_hint"),
          }}
        />
      )}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="payout-batches-corridor"
          className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase"
        >
          {t("list_filter_corridor_label")}
        </label>
        <ColocaSelect
          id="payout-batches-corridor"
          value={corridor ?? ALL_CORRIDORS}
          onValueChange={(value) => onCorridorChange(value === ALL_CORRIDORS ? null : value)}
          options={[{ value: ALL_CORRIDORS, label: t("list_filter_corridor_all") }, ...corridorOptions]}
          aria-label={t("list_filter_corridor_label")}
        />
      </div>
    </div>
  );
}
